import type { IFilter } from '@models/Filter'
import { addWeeks, formatDateToISOWeek } from '@util/date-util'

import { apiClient } from '../../../gateways/api'
import type {
  ProjectenPlanningSearch,
  ProjectenPlanningSearchFilters,
} from '../types/ProjectenPlanningSearch'
import { planningProcesstappen } from '../util/planning-util'

export {
  initialState,
  createFilters,
  searchProjecten,
  DEFAULT_PLANNING_LIMIT,
  PLANNING_LIMIT_OPTIONS,
}

const DEFAULT_PLANNING_WEEKS = 26
const PLANNING_LIMIT_OPTIONS = [100, 250, 500, 1000]
const DEFAULT_PLANNING_LIMIT = PLANNING_LIMIT_OPTIONS[0]

const initialState: ProjectenPlanningSearch = {
  data: { count: 0, skip: 0, limit: 25, items: [] },
  query: {
    filters: {
      combi: null,
      processtap: [],
      gebied: [],
      labels: [],
      fromWeek: formatDateToISOWeek(new Date()),
      toWeek: formatDateToISOWeek(addWeeks(new Date(), DEFAULT_PLANNING_WEEKS)),
      aannemer: null,
      typeWerk: [],
      gemeente: [],
      verantwoordelijke: null,
    },
    sort: { sortBy: 'startUitvoeringWeek', sortDirection: 'asc' },
    page: { skip: 0, limit: DEFAULT_PLANNING_LIMIT },
  },
}

const createFilters = ({
  combi,
  gebied,
  processtap,
  labels,
  fromWeek,
  toWeek,
  aannemer,
  typeWerk,
  gemeente,
  verantwoordelijke,
}: ProjectenPlanningSearchFilters) => {
  const filters: IFilter[] = []

  if (combi)
    filters.push({
      name: 'combi',
      value: combi.code,
      operator: 'eq',
    })

  if (gebied && gebied.length > 0)
    filters.push({
      name: 'gebiedscode',
      value: gebied.map(({ code }) => code),
      operator: 'in',
    })

  if (processtap && processtap.length > 0) {
    filters.push({
      name: 'statusText',
      value: processtap.filter((p) => planningProcesstappen.includes(p)),
      operator: 'in',
    })
  } else {
    filters.push({
      name: 'statusText',
      value: planningProcesstappen,
      operator: 'in',
    })
  }

  if (labels && labels.length > 0)
    filters.push({
      name: 'labels',
      value: labels.map(({ code }) => code),
      operator: 'in',
    })

  if (fromWeek)
    filters.push({
      name: 'eindUitvoeringWeek',
      value: fromWeek,
      operator: 'gte',
    })

  if (toWeek)
    filters.push({
      name: 'startUitvoeringWeek',
      value: toWeek,
      operator: 'lte',
    })

  if (aannemer) {
    filters.push({
      name: 'aannemers',
      operator: 'eq',
      value: aannemer,
    })
  }

  if (Array.isArray(typeWerk) && typeWerk.length > 0) {
    filters.push({
      name: 'typeWerk',
      operator: 'in',
      value: typeWerk,
    })
  }

  if (Array.isArray(gemeente) && gemeente.length > 0) {
    filters.push({
      name: 'gemeente',
      operator: 'in',
      value: gemeente,
    })
  }

  if (verantwoordelijke) {
    filters.push({
      name: 'verantwoordelijken',
      operator: 'eq',
      value: verantwoordelijke.code,
    })
  }

  return filters
}

const searchProjecten = async ({
  query,
}: {
  query: ProjectenPlanningSearch['query']
}): Promise<ProjectenPlanningSearch['data']> => {
  const { filters, page, sort } = query

  const { data } = await apiClient({
    url: '/rest/project/hoofdleiding/search',
    method: 'POST',
    data: { filters: createFilters(filters), ...page, ...sort },
  })

  return data
}
